/**
 * 도시 목록 표기 상한 (지점 다수 파트너 대응).
 *
 * 한 파트너가 247개 지점을 갖고, 한 상품이 60개 도시에 걸쳐 판매되는 케이스가
 * 실제로 존재한다. 이때 도시명을 전부 이어 붙이면
 * - 상세 페이지 `<title>` 이 수백 자가 되어 SERP 에서 잘리고,
 * - 상품 카드 한 줄이 무한정 늘어난다.
 *
 * 그래서 노출은 상위 N개 + "외 M곳" 으로 고정한다. 이 파일이 그 규칙의 단일
 * 출처다 — page.tsx(메타 타이틀) / ProductDetailClient(헤더) / RealProductCard
 * (카드) 세 곳이 모두 여기를 경유한다.
 */

export interface CappedCityList {
  /** 실제로 노출할 도시명 (중복 제거 후 상한 적용). */
  shown: string[];
  /** 상한에서 잘려 나간 도시 수. 0 이면 "외 N곳" 을 붙이지 않는다. */
  extraCount: number;
  /** 중복 제거 후의 전체 도시 수. */
  totalCount: number;
}

/**
 * 도시명을 정리한다: 공백 제거 → 빈 값 제거 → 대소문자 무시 중복 제거.
 *
 * 번역 이후에도 같은 도시가 다른 철자로 들어오는 경우가 있어서
 * ("seoul" / "Seoul") 소문자 키로 비교하되, 노출은 최초 철자를 유지한다.
 */
export function dedupeCityNames(
  cities: Array<string | null | undefined>,
): string[] {
  const seen = new Map<string, string>();
  for (const raw of cities) {
    if (!raw) continue;
    const city = raw.trim();
    if (!city) continue;
    const key = city.toLowerCase();
    if (!seen.has(key)) seen.set(key, city);
  }
  return [...seen.values()];
}

export interface CityCount {
  name: string;
  /** 이 이름으로 들어온 지점 수 (대소문자 무시) */
  count: number;
}

/**
 * `dedupeCityNames` + 지점 수 내림차순 (같은 수는 처음 나온 순서).
 *
 * 지점은 RPC 가 created_at 순으로 주므로, 처음 나온 순서만 쓰면 등록 순서가 지역
 * 줄이 된다 — hinata 캠핑장 113곳(야마나시 19·나가노 11·시즈오카 10)이 "아이치현·
 * 이바라키현·기후현"(2·10·4곳)으로 보였다(10-14 감사 C3).
 */
export function rankCityNamesByCount(cities: Array<string | null | undefined>): CityCount[] {
  const byKey = new Map<string, CityCount>();
  for (const raw of cities) {
    const city = raw?.trim();
    if (!city) continue;
    const key = city.toLowerCase();
    const entry = byKey.get(key);
    if (entry) entry.count += 1;
    else byKey.set(key, { name: city, count: 1 });
  }
  // Array.prototype.sort is stable — ties keep first-seen order.
  return [...byKey.values()].sort((a, b) => b.count - a.count);
}

/**
 * 중복 제거 후 상위 `max` 개만 남긴다.
 *
 * @param max 1 이상. 0 이하가 들어오면 1 로 올린다(도시가 하나도 없는 문자열은
 *            의미가 없다).
 */
export function capCityList(
  cities: Array<string | null | undefined>,
  max: number,
): CappedCityList {
  const unique = dedupeCityNames(cities);
  const limit = Math.max(1, Math.floor(max));
  const shown = unique.slice(0, limit);
  return {
    shown,
    extraCount: Math.max(0, unique.length - shown.length),
    totalCount: unique.length,
  };
}

export interface FormatCityListOptions {
  /** 노출할 도시 수 상한. 타이틀/헤더 3, 카드 1. */
  max: number;
  /**
   * 잘려 나간 수를 표현하는 라벨을 만든다.
   * 보통 `(count) => t("moreCities", { count })` 를 넘긴다.
   * 없으면 "외 N곳" 없이 상위 N개만 이어 붙인다.
   */
  moreLabel?: (count: number) => string;
  /** 도시명 사이 구분자. 기본 ", ". */
  separator?: string;
}

/**
 * `["서울","인천","경기",...60개]` → `"서울, 인천, 경기 외 57곳"`.
 * 도시가 하나도 없으면 빈 문자열.
 */
export function formatCityList(
  cities: Array<string | null | undefined>,
  { max, moreLabel, separator = ", " }: FormatCityListOptions,
): string {
  const { shown, extraCount } = capCityList(cities, max);
  if (shown.length === 0) return "";
  const base = shown.join(separator);
  if (extraCount === 0 || !moreLabel) return base;
  return `${base} ${moreLabel(extraCount)}`;
}

/**
 * 도시 나열 구분자 — 일본어·중국어는 「、」, 그 밖은 ", " (10-13).
 * 東京都, 神奈川県 처럼 쉼표를 쓰면 어색하다.
 */
export function citySeparator(locale: string): string {
  return locale === "ja" || locale.startsWith("zh") ? "、" : ", ";
}

/**
 * 폭에 맞춰 고를 후보 문자열 — `[0]` = 1개 + "외 N곳", 마지막 = 전부(외 N곳 없음).
 * 카드가 한 줄에 들어가는 가장 긴 후보를 고른다(`FittedText`). 도시가 없으면 [].
 */
export function cityListCandidates(
  cities: Array<string | null | undefined>,
  {
    separator = ", ",
    moreLabel,
  }: { separator?: string; moreLabel: (count: number) => string },
): string[] {
  const unique = dedupeCityNames(cities);
  return unique.map((_, index) => {
    const shown = unique.slice(0, index + 1).join(separator);
    const extra = unique.length - (index + 1);
    return extra > 0 ? `${shown} ${moreLabel(extra)}` : shown;
  });
}
